import { useState, useMemo } from 'react';
import {
  retentionEngine,
  type RetentionSchedule,
  type RetentionPolicy,
  type RetentionStatus,
  type DispositionAction,
} from '@/services/compliance/retentionEngine';
import {
  custodyTracking,
  type CustodyChain,
  type CustodyRecord,
  type CustodyMovementType,
} from '@/services/compliance/custodyTracking';
import styles from './CompliancePage.module.css';

type TabType = 'dashboard' | 'retention' | 'custody';

// Mock retention schedules
const generateMockRetentionSchedules = (): RetentionSchedule[] => {
  const schedules: RetentionSchedule[] = [];

  const staffData = [
    { name: 'Kwame Asante', fileNumber: 'GCS-2024-001234', separationDate: null },
    { name: 'Akosua Mensah', fileNumber: 'GCS-2024-001235', separationDate: null },
    { name: 'Kofi Owusu', fileNumber: 'GCS-2024-001236', separationDate: new Date('2022-01-15') },
    { name: 'Ama Serwaa', fileNumber: 'GCS-2024-001237', separationDate: null },
    { name: 'Yaw Boateng', fileNumber: 'GCS-2024-001238', separationDate: new Date('2020-06-30') },
    { name: 'Abena Danso', fileNumber: 'GCS-2024-001239', separationDate: new Date('2018-12-31') },
    { name: 'Kwesi Appiah', fileNumber: 'GCS-2024-001240', separationDate: null },
    { name: 'Efua Mensah', fileNumber: 'GCS-2024-001241', separationDate: new Date('2023-03-15') },
    { name: 'Kofi Asare', fileNumber: 'GCS-2024-001242', separationDate: null },
    { name: 'Adwoa Boateng', fileNumber: 'GCS-2024-001243', separationDate: new Date('2017-08-01') },
  ];

  staffData.forEach((staff, idx) => {
    const schedule = retentionEngine.calculateRetentionSchedule({
      pfileId: `pf_${idx + 1}`,
      staffName: staff.name,
      fileNumber: staff.fileNumber,
      recordType: 'personnel_file',
      createdAt: new Date(2010 + Math.floor(idx / 2), idx % 12, 1),
      separationDate: staff.separationDate || undefined,
      isOnLegalHold: idx === 5,
      legalHoldReason: idx === 5 ? 'Pending litigation - Case No. 2024/HR/001' : undefined,
    });
    schedules.push(schedule);
  });

  return schedules;
};

// Mock custody chains
const generateMockCustodyChains = (): CustodyChain[] => {
  const chains: CustodyChain[] = [];

  const pfiles = [
    { id: 'pf_1', fileNumber: 'GCS-2024-001234', staffName: 'Kwame Asante', isCheckedOut: true },
    { id: 'pf_2', fileNumber: 'GCS-2024-001235', staffName: 'Akosua Mensah', isCheckedOut: false },
    { id: 'pf_3', fileNumber: 'GCS-2024-001236', staffName: 'Kofi Owusu', isCheckedOut: true },
    { id: 'pf_4', fileNumber: 'GCS-2024-001237', staffName: 'Ama Serwaa', isCheckedOut: false },
  ];

  pfiles.forEach(pfile => {
    const records: CustodyRecord[] = [
      {
        id: `custody_1_${pfile.id}`,
        pfileId: pfile.id,
        fileNumber: pfile.fileNumber,
        staffName: pfile.staffName,
        movementType: 'checkout',
        fromLocation: { type: 'registry', mda: 'OHCS', room: 'Registry', cabinet: 'A1' },
        toLocation: { type: 'office', mda: 'OHCS', room: 'HR Office 201' },
        fromCustodian: { id: 'c1', name: 'Registry Officer', role: 'Records Clerk', department: 'Registry', mda: 'OHCS' },
        toCustodian: { id: 'c2', name: 'Kofi Mensah', role: 'HR Officer', department: 'HR', mda: 'OHCS' },
        reason: 'Performance review processing',
        timestamp: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
        verificationMethod: 'signature',
        verifiedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
      },
      ...(pfile.isCheckedOut ? [] : [{
        id: `custody_2_${pfile.id}`,
        pfileId: pfile.id,
        fileNumber: pfile.fileNumber,
        staffName: pfile.staffName,
        movementType: 'checkin' as CustodyMovementType,
        fromLocation: { type: 'office' as const, mda: 'OHCS', room: 'HR Office 201' },
        toLocation: { type: 'registry' as const, mda: 'OHCS', room: 'Registry', cabinet: 'A1' },
        fromCustodian: { id: 'c2', name: 'Kofi Mensah', role: 'HR Officer', department: 'HR', mda: 'OHCS' },
        toCustodian: { id: 'c1', name: 'Registry Officer', role: 'Records Clerk', department: 'Registry', mda: 'OHCS' },
        reason: 'Review completed',
        timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        verificationMethod: 'digital_signature' as const,
        verifiedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      }]),
    ];

    chains.push(custodyTracking.buildCustodyChain(pfile.id, pfile.fileNumber, pfile.staffName, records));
  });

  return chains;
};

export function CompliancePage() {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');

  // Retention state
  const [retentionSchedules] = useState<RetentionSchedule[]>(generateMockRetentionSchedules);
  const [retentionPolicies] = useState<RetentionPolicy[]>(retentionEngine.getPolicies());
  const [retentionFilter, setRetentionFilter] = useState<RetentionStatus | 'all'>('all');
  const [selectedSchedule, setSelectedSchedule] = useState<RetentionSchedule | null>(null);

  // Custody state
  const [custodyChains] = useState<CustodyChain[]>(generateMockCustodyChains);
  const [selectedChain, setSelectedChain] = useState<CustodyChain | null>(null);

  // Computed data
  const retentionSummary = useMemo(() => {
    return retentionEngine.getRetentionSummary(retentionSchedules);
  }, [retentionSchedules]);

  const pendingDisposition = useMemo(() => {
    return retentionEngine.getRecordsPendingDisposition(retentionSchedules);
  }, [retentionSchedules]);

  const approachingDisposition = useMemo(() => {
    return retentionEngine.getRecordsApproachingDisposition(retentionSchedules);
  }, [retentionSchedules]);

  const filteredSchedules = useMemo(() => {
    if (retentionFilter === 'all') return retentionSchedules;
    return retentionSchedules.filter(s => s.status === retentionFilter);
  }, [retentionSchedules, retentionFilter]);

  // Calculate custody stats
  const custodyStats = useMemo(() => {
    const checkedOut = custodyChains.filter(c => c.isCheckedOut).length;
    const withAlerts = custodyChains.filter(c => c.alerts.some(a => !a.isResolved)).length;
    const totalMovements = custodyChains.reduce((sum, c) => sum + c.totalMovements, 0);
    return { checkedOut, withAlerts, totalMovements };
  }, [custodyChains]);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusClass = (status: RetentionStatus) => {
    const classes: Record<RetentionStatus, string> = {
      active: styles.statusActive,
      inactive: styles.statusInactive,
      pending_disposition: styles.statusPending,
      legal_hold: styles.statusHold,
      archived: styles.statusArchived,
      destroyed: styles.statusDestroyed,
    };
    return classes[status];
  };

  const getDispositionClass = (action: DispositionAction) => {
    const classes: Record<DispositionAction, string> = {
      archive_national: styles.dispArchive,
      archive_mda: styles.dispMda,
      destroy_certified: styles.dispDestroy,
      permanent_retention: styles.dispPermanent,
      review_required: styles.dispReview,
    };
    return classes[action];
  };

  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'dashboard', label: 'Compliance Dashboard', icon: '📊' },
    { id: 'retention', label: 'Retention Schedules', icon: '📅' },
    { id: 'custody', label: 'Custody Chain', icon: '🔗' },
  ];

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Compliance & Retention</h1>
          <p className={styles.subtitle}>
            Retention schedules, legal compliance, and custody chain management
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.exportBtn}>
            <span className={styles.btnIcon}>📤</span>
            Export Report
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* Compliance Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className={styles.dashboardTab}>
            {/* Stats Grid */}
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{retentionSummary.total}</div>
                <div className={styles.statLabel}>Total Records</div>
              </div>
              <div className={`${styles.statCard} ${styles.statActive}`}>
                <div className={styles.statValue}>{retentionSummary.active}</div>
                <div className={styles.statLabel}>Active Service</div>
              </div>
              <div className={`${styles.statCard} ${styles.statInactive}`}>
                <div className={styles.statValue}>{retentionSummary.inactive}</div>
                <div className={styles.statLabel}>In Retention</div>
              </div>
              <div className={`${styles.statCard} ${styles.statPending}`}>
                <div className={styles.statValue}>{retentionSummary.pendingDisposition}</div>
                <div className={styles.statLabel}>Pending Disposition</div>
              </div>
              <div className={`${styles.statCard} ${styles.statHold}`}>
                <div className={styles.statValue}>{retentionSummary.onLegalHold}</div>
                <div className={styles.statLabel}>On Legal Hold</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{retentionSummary.approachingDisposition}</div>
                <div className={styles.statLabel}>Approaching Disposition</div>
              </div>
            </div>

            {/* Alert Panels */}
            <div className={styles.alertPanels}>
              {/* Pending Disposition */}
              <div className={styles.alertPanel}>
                <div className={styles.alertHeader}>
                  <h3 className={styles.alertTitle}>
                    <span className={styles.alertIcon}>⚠️</span>
                    Pending Disposition ({pendingDisposition.length})
                  </h3>
                </div>
                <div className={styles.alertList}>
                  {pendingDisposition.length === 0 ? (
                    <p className={styles.noAlerts}>No records pending disposition</p>
                  ) : (
                    pendingDisposition.slice(0, 5).map(schedule => (
                      <div key={schedule.pfileId} className={styles.alertItem}>
                        <div className={styles.alertInfo}>
                          <span className={styles.alertName}>{schedule.staffName}</span>
                          <span className={styles.alertFile}>{schedule.fileNumber}</span>
                        </div>
                        <span className={`${styles.alertAction} ${getDispositionClass(schedule.dispositionAction)}`}>
                          {retentionEngine.getDispositionActionLabel(schedule.dispositionAction)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Approaching Disposition */}
              <div className={styles.alertPanel}>
                <div className={styles.alertHeader}>
                  <h3 className={styles.alertTitle}>
                    <span className={styles.alertIcon}>📆</span>
                    Approaching Disposition ({approachingDisposition.length})
                  </h3>
                </div>
                <div className={styles.alertList}>
                  {approachingDisposition.length === 0 ? (
                    <p className={styles.noAlerts}>No records approaching disposition</p>
                  ) : (
                    approachingDisposition.slice(0, 5).map(schedule => (
                      <div key={schedule.pfileId} className={styles.alertItem}>
                        <div className={styles.alertInfo}>
                          <span className={styles.alertName}>{schedule.staffName}</span>
                          <span className={styles.alertFile}>{schedule.fileNumber}</span>
                        </div>
                        <span className={styles.alertDueDate}>
                          Due: {schedule.retentionEndDate ? formatDate(schedule.retentionEndDate) : 'N/A'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Disposition Actions Breakdown */}
            <div className={styles.breakdownSection}>
              <h3 className={styles.breakdownTitle}>Records by Disposition Action</h3>
              <div className={styles.breakdownGrid}>
                {Object.entries(retentionSummary.byDispositionAction).map(([action, count]) => (
                  <div key={action} className={`${styles.breakdownItem} ${getDispositionClass(action as DispositionAction)}`}>
                    <span className={styles.breakdownCount}>{count}</span>
                    <span className={styles.breakdownLabel}>
                      {retentionEngine.getDispositionActionLabel(action as DispositionAction)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Custody Stats */}
            <div className={styles.custodyStatsSection}>
              <h3 className={styles.sectionTitle}>Custody Overview</h3>
              <div className={styles.custodyStatsGrid}>
                <div className={styles.custodyStat}>
                  <span className={styles.custodyStatIcon}>📁</span>
                  <div>
                    <span className={styles.custodyStatValue}>{custodyStats.checkedOut}</span>
                    <span className={styles.custodyStatLabel}>Files Checked Out</span>
                  </div>
                </div>
                <div className={styles.custodyStat}>
                  <span className={styles.custodyStatIcon}>🔔</span>
                  <div>
                    <span className={styles.custodyStatValue}>{custodyStats.withAlerts}</span>
                    <span className={styles.custodyStatLabel}>With Alerts</span>
                  </div>
                </div>
                <div className={styles.custodyStat}>
                  <span className={styles.custodyStatIcon}>↔️</span>
                  <div>
                    <span className={styles.custodyStatValue}>{custodyStats.totalMovements}</span>
                    <span className={styles.custodyStatLabel}>Total Movements</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Retention Schedules Tab */}
        {activeTab === 'retention' && (
          <div className={styles.retentionTab}>
            {/* Policies Overview */}
            <div className={styles.policiesSection}>
              <h3 className={styles.sectionTitle}>Retention Policies</h3>
              <div className={styles.policiesGrid}>
                {retentionPolicies.slice(0, 4).map(policy => (
                  <div key={policy.id} className={styles.policyCard}>
                    <h4 className={styles.policyName}>{policy.name}</h4>
                    <p className={styles.policyDesc}>{policy.description}</p>
                    <div className={styles.policyDetails}>
                      <span>Active: Lifetime</span>
                      <span>Inactive: {policy.inactiveRetentionYears} years</span>
                    </div>
                    <span className={`${styles.policyAction} ${getDispositionClass(policy.dispositionAction)}`}>
                      {retentionEngine.getDispositionActionLabel(policy.dispositionAction)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Filter */}
            <div className={styles.filterBar}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Status Filter</label>
                <select
                  className={styles.filterSelect}
                  value={retentionFilter}
                  onChange={e => setRetentionFilter(e.target.value as RetentionStatus | 'all')}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Service</option>
                  <option value="inactive">Retention Period</option>
                  <option value="pending_disposition">Pending Disposition</option>
                  <option value="legal_hold">Legal Hold</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <span className={styles.filterCount}>
                Showing {filteredSchedules.length} of {retentionSchedules.length} records
              </span>
            </div>

            {/* Schedules Table */}
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Staff Name</th>
                    <th>File Number</th>
                    <th>Status</th>
                    <th>Separation Date</th>
                    <th>Retention End</th>
                    <th>Disposition</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSchedules.map(schedule => (
                    <tr key={schedule.pfileId}>
                      <td className={styles.nameCell}>{schedule.staffName}</td>
                      <td className={styles.fileCell}>{schedule.fileNumber}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${getStatusClass(schedule.status)}`}>
                          {retentionEngine.getStatusLabel(schedule.status)}
                        </span>
                      </td>
                      <td>{schedule.separationDate ? formatDate(schedule.separationDate) : '—'}</td>
                      <td>{schedule.retentionEndDate ? formatDate(schedule.retentionEndDate) : '—'}</td>
                      <td>
                        <span className={`${styles.dispBadge} ${getDispositionClass(schedule.dispositionAction)}`}>
                          {retentionEngine.getDispositionActionLabel(schedule.dispositionAction)}
                        </span>
                      </td>
                      <td>
                        <button
                          className={styles.viewBtn}
                          onClick={() => setSelectedSchedule(schedule)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Custody Chain Tab */}
        {activeTab === 'custody' && (
          <div className={styles.custodyTab}>
            <div className={styles.custodyList}>
              {custodyChains.map(chain => {
                const riskScore = custodyTracking.calculateRiskScore(chain);
                return (
                  <div
                    key={chain.pfileId}
                    className={`${styles.custodyCard} ${chain.isCheckedOut ? styles.checkedOut : ''}`}
                    onClick={() => setSelectedChain(chain)}
                  >
                    <div className={styles.custodyCardHeader}>
                      <div className={styles.custodyCardInfo}>
                        <span className={styles.custodyCardName}>{chain.staffName}</span>
                        <span className={styles.custodyCardFile}>{chain.fileNumber}</span>
                      </div>
                      <div className={styles.custodyCardStatus}>
                        <span className={`${styles.checkoutBadge} ${chain.isCheckedOut ? styles.out : styles.in}`}>
                          {chain.isCheckedOut ? 'Checked Out' : 'In Registry'}
                        </span>
                        <span className={`${styles.riskBadge} ${styles[`risk${riskScore.level.charAt(0).toUpperCase() + riskScore.level.slice(1)}`]}`}>
                          Risk: {riskScore.level}
                        </span>
                      </div>
                    </div>

                    <div className={styles.custodyCardBody}>
                      <div className={styles.custodyLocation}>
                        <span className={styles.locationIcon}>📍</span>
                        <span>
                          {chain.currentLocation.type === 'registry'
                            ? `${chain.currentLocation.mda} Registry - ${chain.currentLocation.cabinet || 'Main'}`
                            : `${chain.currentLocation.room || chain.currentLocation.description || 'Office'}`}
                        </span>
                      </div>
                      {chain.currentCustodian && (
                        <div className={styles.custodyCustodian}>
                          <span className={styles.custodianIcon}>👤</span>
                          <span>
                            {chain.currentCustodian.name} ({chain.currentCustodian.role})
                          </span>
                        </div>
                      )}
                    </div>

                    <div className={styles.custodyCardFooter}>
                      <span className={styles.movementCount}>
                        {chain.totalMovements} movement{chain.totalMovements !== 1 ? 's' : ''}
                      </span>
                      {chain.alerts.filter(a => !a.isResolved).length > 0 && (
                        <span className={styles.alertCount}>
                          {chain.alerts.filter(a => !a.isResolved).length} alert(s)
                        </span>
                      )}
                      <span className={styles.viewChain}>View chain →</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Retention Schedule Modal */}
      {selectedSchedule && (
        <div className={styles.modalOverlay} onClick={() => setSelectedSchedule(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>{selectedSchedule.staffName}</h2>
                <p className={styles.modalSubtitle}>{selectedSchedule.fileNumber}</p>
              </div>
              <button className={styles.modalClose} onClick={() => setSelectedSchedule(null)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.scheduleOverview}>
                <div className={styles.overviewRow}>
                  <span className={styles.overviewLabel}>Status</span>
                  <span className={`${styles.statusBadge} ${getStatusClass(selectedSchedule.status)}`}>
                    {retentionEngine.getStatusLabel(selectedSchedule.status)}
                  </span>
                </div>
                <div className={styles.overviewRow}>
                  <span className={styles.overviewLabel}>Policy</span>
                  <span>{selectedSchedule.policyName}</span>
                </div>
                <div className={styles.overviewRow}>
                  <span className={styles.overviewLabel}>Created</span>
                  <span>{formatDate(selectedSchedule.createdAt)}</span>
                </div>
                {selectedSchedule.separationDate && (
                  <div className={styles.overviewRow}>
                    <span className={styles.overviewLabel}>Separation Date</span>
                    <span>{formatDate(selectedSchedule.separationDate)}</span>
                  </div>
                )}
                {selectedSchedule.retentionEndDate && (
                  <div className={styles.overviewRow}>
                    <span className={styles.overviewLabel}>Retention End Date</span>
                    <span>{formatDate(selectedSchedule.retentionEndDate)}</span>
                  </div>
                )}
                <div className={styles.overviewRow}>
                  <span className={styles.overviewLabel}>Disposition Action</span>
                  <span className={`${styles.dispBadge} ${getDispositionClass(selectedSchedule.dispositionAction)}`}>
                    {retentionEngine.getDispositionActionLabel(selectedSchedule.dispositionAction)}
                  </span>
                </div>
                {selectedSchedule.isOnLegalHold && (
                  <div className={styles.legalHoldAlert}>
                    <span className={styles.holdIcon}>⚖️</span>
                    <div>
                      <strong>Legal Hold Active</strong>
                      <p>{selectedSchedule.legalHoldReason}</p>
                    </div>
                  </div>
                )}
              </div>

              {selectedSchedule.notifications.length > 0 && (
                <div className={styles.notificationsSection}>
                  <h4>Notifications</h4>
                  {selectedSchedule.notifications.map(notif => (
                    <div key={notif.id} className={styles.notification}>
                      <span className={styles.notifType}>{notif.type.replace('_', ' ')}</span>
                      <span className={styles.notifMessage}>{notif.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custody Chain Modal */}
      {selectedChain && (
        <div className={styles.modalOverlay} onClick={() => setSelectedChain(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>{selectedChain.staffName}</h2>
                <p className={styles.modalSubtitle}>{selectedChain.fileNumber}</p>
              </div>
              <button className={styles.modalClose} onClick={() => setSelectedChain(null)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.chainOverview}>
                <div className={styles.chainStat}>
                  <span className={styles.chainStatLabel}>Status</span>
                  <span className={`${styles.checkoutBadge} ${selectedChain.isCheckedOut ? styles.out : styles.in}`}>
                    {selectedChain.isCheckedOut ? 'Checked Out' : 'In Registry'}
                  </span>
                </div>
                <div className={styles.chainStat}>
                  <span className={styles.chainStatLabel}>Total Movements</span>
                  <span className={styles.chainStatValue}>{selectedChain.totalMovements}</span>
                </div>
                <div className={styles.chainStat}>
                  <span className={styles.chainStatLabel}>Avg. Duration</span>
                  <span className={styles.chainStatValue}>{selectedChain.averageCustodyDuration} days</span>
                </div>
              </div>

              {selectedChain.alerts.filter(a => !a.isResolved).length > 0 && (
                <div className={styles.chainAlerts}>
                  <h4>Active Alerts</h4>
                  {selectedChain.alerts.filter(a => !a.isResolved).map(alert => (
                    <div key={alert.id} className={`${styles.chainAlert} ${styles[`alert${alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}`]}`}>
                      <span className={styles.chainAlertType}>{alert.type.replace('_', ' ')}</span>
                      <span className={styles.chainAlertMessage}>{alert.message}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.chainTimeline}>
                <h4>Custody Chain</h4>
                <div className={styles.timeline}>
                  {selectedChain.chain.map((record, idx) => (
                    <div key={record.id} className={styles.timelineItem}>
                      <div className={styles.timelineMarker}>
                        <span className={styles.timelineIcon}>
                          {custodyTracking.getMovementTypeIcon(record.movementType)}
                        </span>
                        {idx < selectedChain.chain.length - 1 && <div className={styles.timelineLine} />}
                      </div>
                      <div className={styles.timelineContent}>
                        <div className={styles.timelineHeader}>
                          <span className={styles.timelineType}>
                            {custodyTracking.getMovementTypeLabel(record.movementType)}
                          </span>
                          <span className={styles.timelineDate}>{formatDate(record.timestamp)}</span>
                        </div>
                        <p className={styles.timelineDesc}>
                          {record.fromCustodian.name} → {record.toCustodian.name}
                        </p>
                        <p className={styles.timelineReason}>{record.reason}</p>
                        {record.verifiedAt && (
                          <span className={styles.verifiedBadge}>
                            ✓ Verified ({record.verificationMethod})
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
